import Vue from 'vue'

import {
    ALLOWED_TOP_STATUSES,
    ROUTE_STATUSES_FILTER,
    STAFF_ALLOWED_TOP_STATUSES,
} from '@/modules/dispatcher/constants/filters'
import {
    handleRouteError,
    isCancelledError,
} from '@/modules/dispatcher/utils/errors'
import {
    cleanupExpiredGroups,
    loadPrintGroups,
    savePrintGroups,
} from '@/modules/dispatcher/utils/printGroupsStorage'
import { fetchCouriers } from '@/services/couriersService'
import {
    fetchOrderById,
    fetchOrders,
    updateOrder,
} from '@/services/orderService'
import { fetchStatusesCount } from '@/services/orderStatusesService'
import {
    addOrdersToRoute,
    assignCourier,
    completeRoute,
    createRoute,
    deleteRoute,
    fetchRoutes,
    fetchRouteStatuses,
    removeOrdersFromRoute,
    startRoute,
    transferOrdersToRoute,
    unassignCourier,
} from '@/services/routesService'
import { $themeConfig } from '@themeConfig'

const BACK_URL = $themeConfig.app.backUrl

export default {
    async fetchOrders({ commit }, { offset, limit, paramsSearch, signal }) {
        commit('SET_LOADING', true)
        try {
            const response = await fetchOrders(
                offset,
                limit,
                paramsSearch,
                signal
            )
            commit('SET_ORDERS', response.data?.orders ?? [])
            commit('SET_TOTAL_COUNT', response.data?.count ?? 0)
        } catch (error) {
            if (isCancelledError(error)) {
                return
            }
            console.error('Ошибка при получении списка заказов', error)
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async fetchOrderById({ commit }, id) {
        commit('SET_LOADING', true)
        try {
            const response = await fetchOrderById(id)
            commit('SET_CURRENT_ORDER', response.data?.order ?? null)
            return response.data
        } catch (error) {
            console.error('Ошибка при получении заказа по id', error)
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async fetchOrderChanges({ commit }, { id, limit = 15, offset = 0 } = {}) {
        commit('SET_ORDER_CHANGES_LOADING', true)
        try {
            const res = await Vue.axios.get(`${BACK_URL}/changes`, {
                params: { order_id: id, limit, offset },
            })

            const data = res?.data ?? {}
            const changes = data.changes ?? []
            const total = data.count ?? 0

            commit('SET_ORDER_CHANGES', Array.isArray(changes) ? changes : [])
            commit('SET_ORDER_CHANGES_TOTAL', Number(total) || 0)

            return res.data
        } finally {
            commit('SET_ORDER_CHANGES_LOADING', false)
        }
    },

    async updateOrder(_, { id, body }) {
        try {
            return await updateOrder(id, body)
        } catch (error) {
            console.error('Ошибка при изменении комментария заказа', error)
            throw error
        }
    },

    async fetchCouriers({ commit }, divisionId) {
        try {
            const response = await fetchCouriers(divisionId)
            commit('SET_COURIERS', response.data?.couriers ?? [])
        } catch (error) {
            console.error('Ошибка при получении списка курьеров', error)
            throw error
        }
    },

    async assignCourier({ commit }, { routeId, courierId }) {
        try {
            const response = await assignCourier(routeId, courierId)
            return response.data ?? null
        } catch (error) {
            console.error('Ошибка при назначении курьера', error)
            throw error
        }
    },

    async unassignCourier({ commit }, { routeId }) {
        try {
            const response = await unassignCourier(routeId)
            return response.data ?? null
        } catch (error) {
            console.error('Ошибка при снятии курьера с маршрута', error)
            throw error
        }
    },

    async fetchStatusesCount({ commit }, { paramsSearch, signal }) {
        try {
            const response = await fetchStatusesCount(paramsSearch, signal)
            const statuses = response.data ?? []

            const topStatuses =
                paramsSearch.type !== 'staff'
                    ? ALLOWED_TOP_STATUSES
                    : STAFF_ALLOWED_TOP_STATUSES

            const fullStatuses = topStatuses.map(allowedStatus => {
                const serverStatus = statuses.find(
                    s => s.id === allowedStatus.id
                )
                if (serverStatus) {
                    return {
                        ...allowedStatus,
                        count: serverStatus.count,
                    }
                }

                return {
                    ...allowedStatus,
                    count: 0,
                }
            })

            commit('SET_STATUSES_COUNT', fullStatuses)
        } catch (error) {
            if (isCancelledError(error)) {
                return
            }
            console.error('Ошибка при получении списка статусов', error)
            throw error
        }
    },

    initPrintGroups({ commit }) {
        const groups = loadPrintGroups()
        const cleanedGroups = cleanupExpiredGroups(groups)

        // Если были удалены устаревшие группы, сохраняем очищенный результат
        if (Object.keys(groups).length !== Object.keys(cleanedGroups).length) {
            savePrintGroups(cleanedGroups)
        }

        commit('SET_PRINT_GROUPS', cleanedGroups)
    },

    async fetchRoutes(
        { commit },
        {
            end_date,
            start_date,
            status,
            courier_id,
            page,
            page_size,
            divisionId,
            order_number,
        }
    ) {
        commit('SET_ROUTES_LOADING', true)
        try {
            const response = await fetchRoutes(divisionId, {
                end_date,
                start_date,
                status,
                courier_id,
                page,
                page_size,
                order_number,
            })

            const payload = response.data ?? {}

            commit('SET_ROUTES', payload.data ?? [])
            commit('SET_ROUTES_COUNT', payload.pagination?.total ?? 0)
        } catch (error) {
            console.error('Ошибка при получении списка маршрутов', error)
            throw error
        } finally {
            commit('SET_ROUTES_LOADING', false)
        }
    },

    // Routes
    async createRoute({ commit }, { date, divisionId }) {
        commit('SET_ROUTES_LOADING', true)
        try {
            const response = await createRoute(divisionId, { date })
            return response.data
        } catch (error) {
            console.error('Ошибка при создании маршрута', error)
            throw error
        } finally {
            commit('SET_ROUTES_LOADING', false)
        }
    },

    async deleteRoute({ commit }, { id }) {
        commit('SET_ROUTES_LOADING', true)
        try {
            await deleteRoute(id)
            commit('REMOVE_ROUTE', id)
        } catch (error) {
            handleRouteError(error, 'deleteRoute')
        } finally {
            commit('SET_ROUTES_LOADING', false)
        }
    },

    async fetchRouteStatusesCount(
        { commit },
        { end_date, start_date, courier_id, divisionId, order_number } = {}
    ) {
        commit('SET_ROUTE_STATUSES_LOADING', true)
        try {
            const response = await fetchRouteStatuses(divisionId, {
                end_date,
                start_date,
                courier_id,
                order_number,
            })

            const serverStatuses = response.data || []

            const normalized = ROUTE_STATUSES_FILTER.map(s => {
                const fromServer = serverStatuses.find(x => x.id === s.id)
                return {
                    ...s,
                    name: fromServer?.name ?? s.name,
                    count: Number(fromServer?.count) || 0,
                }
            })

            commit('SET_ROUTE_STATUSES_COUNT', normalized)
        } catch (error) {
            console.error('Ошибка при получении статусов маршрутов', error)
            throw error
        } finally {
            commit('SET_ROUTE_STATUSES_LOADING', false)
        }
    },

    async addOrdersToRoute({ commit }, { routeId, orderIds, idempotencyKey }) {
        try {
            const response = await addOrdersToRoute(
                routeId,
                orderIds,
                idempotencyKey
            )
            return response.data ?? null
        } catch (error) {
            return handleRouteError(error, 'addOrdersToRoute')
        }
    },

    async removeOrdersFromRoute({ commit }, { routeId, orderIds }) {
        try {
            const response = await removeOrdersFromRoute(routeId, orderIds)
            return response.data ?? null
        } catch (error) {
            return handleRouteError(error, 'removeOrdersFromRoute')
        }
    },

    async transferOrdersToRoute(
        { commit },
        { sourceRouteId, targetRouteId, orderIds, idempotencyKey }
    ) {
        commit('SET_ROUTES_LOADING', true)
        try {
            const response = await transferOrdersToRoute(
                sourceRouteId,
                targetRouteId,
                orderIds,
                idempotencyKey
            )
            return response.data
        } catch (error) {
            return handleRouteError(error, 'transferOrdersToRoute')
        } finally {
            commit('SET_ROUTES_LOADING', false)
        }
    },

    async startRoute({ commit }, { routeId, orders }) {
        try {
            const response = await startRoute(routeId, orders)
            const updatedRoute = response.data ?? null
            if (updatedRoute?.id) {
                commit('UPDATE_ROUTE', updatedRoute)
            }
            return updatedRoute
        } catch (error) {
            return handleRouteError(error, 'startRoute')
        }
    },

    async completeRoute({ commit }, { routeId, force = false }) {
        try {
            const response = await completeRoute(routeId, { force })
            const updatedRoute = response.data ?? null
            if (updatedRoute?.id) {
                commit('UPDATE_ROUTE', updatedRoute)
            }
            return updatedRoute
        } catch (error) {
            return handleRouteError(error, 'completeRoute')
        }
    },
}
